import React, { useState, useRef } from 'react';
import { bool, func, object, arrayOf } from 'prop-types';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import { audienceImportErrorKey } from '../../util/audienceImportError';
import Modal from '../Modal/Modal';
import ConsentBasisFields, {
  attestationFromValues,
} from '../ConsentBasisFields/ConsentBasisFields';
import { AddAudienceByEmailForm } from '../../forms';
import css from './AddMembersToListModal.module.css';

// Focused "add members to an audience list" modal. Reuses the same
// email + upload paths the full EditListPage uses, minus the list-details
// (rename) section. Wired from CreatorAudiencePage so the underlying
// duck actions (onAddMembers, onImportMembers) stay in one place.
const AddMembersToListModal = props => {
  const {
    isOpen,
    onClose,
    list,
    availableLists,
    onAddMembers,
    onImportMembers,
    onGetListDetail,
    addMembersInProgress,
    importInProgress,
    importError,
    onManageDisableScrolling,
    intl,
  } = props;

  const [isDragging, setIsDragging] = useState(false);
  // A CSV import has no form of its own; its consent attestation comes from
  // the small form above the drop zone, mirrored into state via FormSpy.
  const [importAttestation, setImportAttestation] = useState(null);
  const [addSuccess, setAddSuccess] = useState(false);
  const [importSuccess, setImportSuccess] = useState(false);
  const [selectedSourceId, setSelectedSourceId] = useState('');
  const [fromListInProgress, setFromListInProgress] = useState(false);
  const [fromListSuccess, setFromListSuccess] = useState(false);
  const [fromListError, setFromListError] = useState(null);
  const fileInputRef = useRef(null);

  // Every owned list except the one we're adding TO. Rented / Gig Boost
  // lists are excluded — they're read-only, and their members are
  // scope-restricted by design.
  const sourceLists = (availableLists || []).filter(
    l => l.id !== list?.id && l.provenance?.kind !== 'rented'
  );

  const handleAddFromList = async () => {
    if (!selectedSourceId || !list || !onGetListDetail || !onAddMembers) return;
    setFromListInProgress(true);
    setFromListError(null);
    try {
      const sourceList = await onGetListDetail(selectedSourceId);
      const emails = (sourceList?.members || []).map(m => m.email).filter(Boolean);
      if (emails.length === 0) {
        setFromListError(intl.formatMessage({ id: 'AddMembersToListModal.fromListEmpty' }));
        setFromListInProgress(false);
        return;
      }
      await onAddMembers(list.id, emails);
      setFromListSuccess(true);
      setSelectedSourceId('');
      setTimeout(() => setFromListSuccess(false), 2000);
    } catch (err) {
      setFromListError(intl.formatMessage({ id: 'AddMembersToListModal.fromListError' }));
    } finally {
      setFromListInProgress(false);
    }
  };

  const handleAddByEmail = async values => {
    if (!values.emails?.trim() || !onAddMembers || !list) return;
    const emailList = values.emails
      .split(',')
      .map(e => e.trim())
      .filter(Boolean);
    const attestation = attestationFromValues(values);
    if (!attestation) return;
    try {
      await onAddMembers(list.id, emailList, attestation);
      setAddSuccess(true);
      setTimeout(() => setAddSuccess(false), 2000);
    } catch (err) {
      // Error surfaced by parent — nothing to do here.
    }
  };

  const handleFileUpload = file => {
    // No attestation, no import: the server would refuse it anyway.
    if (!file || !onImportMembers || !list || !importAttestation) return;
    const validExtensions = ['.csv', '.xlsx', '.ods'];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!validExtensions.includes(ext)) {
      // eslint-disable-next-line no-alert
      alert(intl.formatMessage({ id: 'AddMembersToListModal.invalidFileType' }));
      return;
    }
    const reader = new FileReader();
    reader.onload = async e => {
      try {
        await onImportMembers(
          list.id,
          { fileName: file.name, data: e.target.result },
          importAttestation
        );
        setImportSuccess(true);
        setTimeout(() => setImportSuccess(false), 2000);
      } catch (err) {
        // The refusal is in the state, and importError shows it
      }
    };
    reader.readAsText(file);
  };

  const handleDragOver = e => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = e => {
    e.preventDefault();
    setIsDragging(false);
    handleFileUpload(e.dataTransfer.files[0]);
  };
  const handleFileInputChange = e => {
    handleFileUpload(e.target.files[0]);
    e.target.value = '';
  };

  return (
    <Modal
      id="AddMembersToListModal"
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      containerClassName={css.container}
      usePortal
    >
      <div className={css.root}>
        <h2 className={css.title}>{intl.formatMessage({ id: 'AddMembersToListModal.title' })}</h2>
        <p className={css.subtitle}>
          {intl.formatMessage(
            { id: 'AddMembersToListModal.subtitle' },
            { listName: list?.name || '' }
          )}
        </p>

        <div className={css.section}>
          <h3 className={css.sectionTitle}>
            {intl.formatMessage({ id: 'AddMembersToListModal.addByEmail' })}
          </h3>
          <AddAudienceByEmailForm
            onSubmit={handleAddByEmail}
            inProgress={addMembersInProgress}
            ready={addSuccess}
          />
        </div>

        {sourceLists.length > 0 && (
          <div className={css.section}>
            <h3 className={css.sectionTitle}>
              {intl.formatMessage({ id: 'AddMembersToListModal.addFromList' })}
            </h3>
            <div className={css.fromListRow}>
              <select
                className={css.fromListSelect}
                value={selectedSourceId}
                onChange={e => setSelectedSourceId(e.target.value)}
                disabled={fromListInProgress || fromListSuccess}
              >
                <option value="">
                  {intl.formatMessage({ id: 'AddMembersToListModal.fromListPlaceholder' })}
                </option>
                {sourceLists.map(l => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                    {typeof l.memberCount === 'number' ? ` (${l.memberCount})` : ''}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className={`${css.chooseFileButton} ${
                  fromListSuccess ? css.chooseFileButtonSuccess : ''
                }`}
                onClick={handleAddFromList}
                disabled={!selectedSourceId || fromListInProgress || fromListSuccess}
              >
                {fromListSuccess
                  ? intl.formatMessage({ id: 'AddMembersToListModal.fromListDone' })
                  : fromListInProgress
                  ? intl.formatMessage({ id: 'AddMembersToListModal.fromListAdding' })
                  : intl.formatMessage({ id: 'AddMembersToListModal.fromListAdd' })}
              </button>
            </div>
            {fromListError && <p className={css.fromListError}>{fromListError}</p>}
          </div>
        )}

        <div className={css.section}>
          <h3 className={css.sectionTitle}>
            {intl.formatMessage({ id: 'AddMembersToListModal.addByUpload' })}
          </h3>
          <FinalForm
            onSubmit={() => {}}
            render={({ values }) => (
              <>
                <FormSpy
                  subscription={{ values: true }}
                  onChange={({ values: v }) => setImportAttestation(attestationFromValues(v))}
                />
                <ConsentBasisFields
                  values={values}
                  idPrefix="listImport"
                  className={css.uploadAttestation}
                />
              </>
            )}
          />
          <div
            className={`${css.uploadZone} ${isDragging ? css.uploadZoneDragging : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className={css.uploadText}>
              <p className={css.uploadTitle}>
                {intl.formatMessage({ id: 'AddMembersToListModal.dragFiles' })}
              </p>
              <p className={css.uploadHint}>
                {intl.formatMessage({ id: 'AddMembersToListModal.fileTypes' })}
              </p>
            </div>
            <button
              type="button"
              className={`${css.chooseFileButton} ${
                importSuccess ? css.chooseFileButtonSuccess : ''
              }`}
              onClick={() => fileInputRef.current?.click()}
              disabled={importInProgress || importSuccess || !importAttestation}
            >
              {importSuccess
                ? intl.formatMessage({ id: 'AddMembersToListModal.uploaded' })
                : importInProgress
                ? intl.formatMessage({ id: 'AddMembersToListModal.uploading' })
                : intl.formatMessage({ id: 'AddMembersToListModal.chooseFile' })}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.ods"
              className={css.fileInput}
              onChange={handleFileInputChange}
            />
            {isOpen && importError ? (
              <p className={css.importError} role="alert">
                {intl.formatMessage({ id: audienceImportErrorKey(importError) })}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </Modal>
  );
};

AddMembersToListModal.defaultProps = {
  list: null,
  availableLists: [],
  onGetListDetail: null,
  addMembersInProgress: false,
  importInProgress: false,
  importError: null,
};

AddMembersToListModal.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  list: object,
  availableLists: arrayOf(object),
  onAddMembers: func.isRequired,
  onImportMembers: func.isRequired,
  // Async — takes a listId, resolves to that list's detail (must
  // include .members[].email). Only required when availableLists is
  // non-empty, but declared here so callers see the contract.
  onGetListDetail: func,
  addMembersInProgress: bool,
  importInProgress: bool,
  importError: object,
  onManageDisableScrolling: func.isRequired,
  intl: intlShape.isRequired,
};

export default injectIntl(AddMembersToListModal);
